/* ==========================================================
   Shipping Widget — countdown (EST/EDT) + cart progress
   ========================================================== */

(function () {
  "use strict";

  // ── Helpers ───────────────────────────────────────────────

  function formatMoney(cents) {
    const dollars = cents / 100;
    return "$" + (Number.isInteger(dollars) ? dollars : dollars.toFixed(2));
  }

  function padTwo(n) {
    return String(Math.floor(n)).padStart(2, "0");
  }

  // ── EST / EDT time helpers ────────────────────────────────
  // Uses Intl.DateTimeFormat with America/New_York so DST is
  // handled automatically — no manual UTC offset needed.

  function getNYParts() {
    const now = new Date();
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).formatToParts(now);
    const get = (t) => parseInt(parts.find((p) => p.type === t).value, 10);
    return { h: get("hour"), m: get("minute"), s: get("second") };
  }

  function isPastCutoff(cutoffHour, cutoffMinute) {
    const { h, m } = getNYParts();
    return h > cutoffHour || (h === cutoffHour && m >= cutoffMinute);
  }

  /** Seconds remaining until the next cutoff, computed in NY time */
  function secsUntilCutoff(cutoffHour, cutoffMinute) {
    const { h, m, s } = getNYParts();
    const nowSecs = h * 3600 + m * 60 + s;
    const cutoffSecs = cutoffHour * 3600 + cutoffMinute * 60;
    let diff = cutoffSecs - nowSecs;
    if (diff <= 0) diff += 86400; // wrap to next day
    return diff;
  }

  // ── Countdown ─────────────────────────────────────────────

  function runCountdown(widget, cutoffHour, cutoffMinute) {
    const countdownEl = widget.querySelector("[data-sw-countdown]");
    const labelEl     = widget.querySelector("[data-sw-timer-label]");
    const subEl       = widget.querySelector("[data-sw-timer-sub]");

    if (!countdownEl) return;

    function showAfterCutoff() {
      countdownEl.hidden = true;
      if (labelEl) labelEl.textContent = widget.dataset.afterCutoffLine1 ?? "";
      if (subEl)   subEl.textContent   = widget.dataset.afterCutoffLine2 ?? "";
    }

    function tick() {
      if (isPastCutoff(cutoffHour, cutoffMinute)) {
        showAfterCutoff();
        return; // stop ticking
      }

      const total = secsUntilCutoff(cutoffHour, cutoffMinute);
      const h = Math.floor(total / 3600);
      const m = Math.floor((total % 3600) / 60);
      const s = total % 60;
      countdownEl.textContent = padTwo(h) + ":" + padTwo(m) + ":" + padTwo(s);

      // Schedule next tick aligned to the next whole second
      setTimeout(tick, 1000 - (Date.now() % 1000));
    }

    tick();
  }

  // ── Cart progress ─────────────────────────────────────────

  function updateProgress(widget, cartCents) {
    const threshold = parseInt(widget.dataset.threshold || "20000", 10);
    const remaining = Math.max(0, threshold - cartCents);
    const progress  = Math.min(100, Math.round((cartCents / threshold) * 100));

    const fill  = widget.querySelector("[data-sw-bar]");
    const thumb = widget.querySelector("[data-sw-thumb]");
    if (fill)  fill.style.width = progress + "%";
    if (thumb) thumb.style.left = progress + "%";

    const msgEl = widget.querySelector(".shipping-widget__progress-msg");
    if (msgEl) {
      msgEl.innerHTML =
        remaining > 0
          ? 'You\'re <strong class="shipping-widget__highlight" data-sw-remaining>' +
            formatMoney(remaining) +
            "</strong> away from free shipping!"
          : '<strong class="shipping-widget__highlight">You\'ve unlocked free shipping!</strong>';
    }

    const cartLabel = widget.querySelector("[data-sw-cart-label]");
    const toGoLabel = widget.querySelector("[data-sw-to-go]");
    if (cartLabel) cartLabel.textContent = formatMoney(cartCents) + " in Cart";
    if (toGoLabel) {
      toGoLabel.textContent =
        remaining > 0 ? formatMoney(remaining) + " to go" : "Free shipping!";
    }
  }

  // ── Cart fetch ────────────────────────────────────────────

  function fetchAndUpdateAll() {
    fetch("/cart.js")
      .then((r) => r.json())
      .then((data) => {
        document
          .querySelectorAll(".shipping-widget")
          .forEach((w) => updateProgress(w, data.total_price));
      })
      .catch(() => {});
  }

  // ── Patch window.fetch once for all widgets ───────────────

  function patchFetch() {
    if (window.__swFetchPatched) return;
    window.__swFetchPatched = true;

    const origFetch = window.fetch;
    window.fetch = function (input, init) {
      const url =
        typeof input === "string" ? input : (input && input.url) || "";
      const result = origFetch.apply(this, arguments);

      if (
        url.includes("/cart/add") ||
        url.includes("/cart/change") ||
        url.includes("/cart/update")
      ) {
        result
          .then((r) => r.clone().json())
          .then(fetchAndUpdateAll)
          .catch(() => {});
      }
      return result;
    };
  }

  // ── Geo check (IP-based via Shopify) ─────────────────────
  // /browsing_context_suggestions.json returns the visitor's
  // detected country from Shopify's CDN — no Markets setup needed.

  function resolveGeo() {
    return fetch("/browsing_context_suggestions.json")
      .then((r) => r.json())
      .then((data) => {
        return (
          data?.detected_values?.country?.handle ||
          data?.country_code ||
          ""
        ).toUpperCase();
      })
      .catch(() => "");
  }

  // ── Init ──────────────────────────────────────────────────

  function initWidget(widget) {
    const cutoffHour   = parseInt(widget.dataset.cutoffHour   || "15", 10);
    const cutoffMinute = parseInt(widget.dataset.cutoffMinute || "0",  10);

    // Show widget (geo already confirmed or not required)
    widget.style.display = "";
    runCountdown(widget, cutoffHour, cutoffMinute);
  }

  function boot() {
    const widgets = document.querySelectorAll(".shipping-widget");
    if (!widgets.length) return;

    const usaOnlyWidgets = Array.from(widgets).filter(
      (w) => w.dataset.usaOnly === "true"
    );
    const openWidgets = Array.from(widgets).filter(
      (w) => w.dataset.usaOnly !== "true"
    );

    // Widgets with no geo restriction — init immediately
    openWidgets.forEach(initWidget);

    // Widgets with USA-only restriction — check geo first
    if (usaOnlyWidgets.length) {
      resolveGeo().then((countryCode) => {
        if (countryCode === "US") {
          usaOnlyWidgets.forEach(initWidget);
        }
        // If not US: widgets stay hidden (display:none from CSS)
      });
    }

    // Cart event listeners (theme events)
    document.addEventListener("cart:updated",         fetchAndUpdateAll);
    document.addEventListener("cart-drawer:refreshed", fetchAndUpdateAll);

    // Fallback: intercept fetch-based cart mutations
    patchFetch();

    // Fallback: form submit delay (covers non-fetch add-to-cart)
    document.querySelectorAll("[data-type='add-to-cart-form']").forEach((form) => {
      form.addEventListener("submit", () => setTimeout(fetchAndUpdateAll, 900));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();
